import { useState, useEffect, createContext } from "react";
import { useAccount } from "wagmi";
import { Header } from "./components/Header";
import { CreateVote } from "./components/CreateVote";
import { VoteList } from "./components/VoteList";
import { Dashboard } from "./components/Dashboard";
import { DecryptSpotlight } from "./components/DecryptSpotlight";
import { useFhevm } from "./fhevm/useFhevm";
import { useMetaMaskProvider } from "./hooks/useMetaMaskProvider";
import { usePollInsights } from "./hooks/usePollInsights";
import type { FhevmInstance } from "./fhevm/fhevmTypes";
import type { FhevmGoState } from "./fhevm/useFhevm";
import "./App.css";

interface FhevmContextValue {
  instance: FhevmInstance | undefined;
  status: FhevmGoState;
  error: Error | undefined;
}

export const FhevmContext = createContext<FhevmContextValue>({
  instance: undefined,
  status: "idle",
  error: undefined,
});

const pageSections = [
  { id: "overview", label: "Mission", icon: "🌌", description: "High-level controls" },
  { id: "polls", label: "Poll Deck", icon: "🗳️", description: "Encrypted ballots" },
  { id: "launch", label: "Create Poll", icon: "🚀", description: "Start a new vote" },
  { id: "insights", label: "Insights", icon: "📊", description: "On-chain metrics" },
] as const;

type SectionId = (typeof pageSections)[number]["id"];

function App() {
  const { isConnected, chainId } = useAccount();
  const provider = useMetaMaskProvider();
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeSection, setActiveSection] = useState<SectionId>("overview");
  const [panelState, setPanelState] = useState("settled");

  const { instance, status, error } = useFhevm({
    provider,
    chainId,
    enabled: isConnected,
  });

  const insights = usePollInsights(refreshKey);

  useEffect(() => {
    setPanelState("entering");
    const timer = window.setTimeout(() => setPanelState("settled"), 420);
    return () => window.clearTimeout(timer);
  }, [activeSection]);

  const handleVoteCreated = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const networkName =
    chainId === 11155111
      ? "Sepolia Testnet"
      : chainId === 31337
      ? "Hardhat Local"
      : "Unsupported network";

  const fhevmContextValue: FhevmContextValue = {
    instance,
    status,
    error,
  };

  const renderSection = () => {
    switch (activeSection) {
      case "overview":
        return (
          <div className="overview-grid">
            <section className="hero-card">
              <p className="hero-kicker">Mission Control</p>
              <h2>
                Securely orchestrate encrypted ballots across {networkName} with FHE-anchored privacy.
              </h2>
              <p className="hero-body">
                {insights.stats
                  ? `${insights.stats.totalPolls} sealed polls · ${insights.stats.decryptedPolls} decrypted`
                  : "Connect your wallet to unlock live telemetry and visual decryptions."}
              </p>
              <div className="hero-badges">
                <span>FHE ready</span>
                <span>Time locked</span>
                <span>{networkName}</span>
                <span>{insights.stats ? `${insights.stats.totalVoters} voters` : "Awaiting votes"}</span>
              </div>
              <div className="hero-meta">
                <span className="network-pill">{networkName}</span>
                <span className="meta-value">
                  {insights.stats ? `${insights.stats.decryptedPolls} decrypted` : "Decryptions pending"}
                </span>
              </div>
              {insights.error && <p className="hero-error">{insights.error}</p>}
            </section>

            <DecryptSpotlight highlight={insights.stats?.highlight ?? null} />
          </div>
        );

      case "polls":
        return (
          <div className="page-panel-card">
            <h2 className="page-panel-title">
              <span className="title-icon">🗳️</span>
              Active Polls
            </h2>
            <VoteList refresh={refreshKey} />
          </div>
        );

      case "launch":
        return (
          <div className="page-panel-card">
            <h2 className="page-panel-title">
              <span className="title-icon">🚀</span>
              Launch New Poll
            </h2>
            <CreateVote onVoteCreated={handleVoteCreated} />
          </div>
        );

      case "insights":
        return (
          <Dashboard stats={insights.stats} loading={insights.loading} error={insights.error} />
        );

      default:
        return null;
    }
  };

  return (
    <FhevmContext.Provider value={fhevmContextValue}>
      <div className="app">
        <Header />

        {isConnected && status === "loading" && (
          <div className="fhevm-status">
            <div className="fhevm-status-content">
              <div className="loader"></div>
              <p>Initializing FHEVM...</p>
            </div>
          </div>
        )}

        {isConnected && status === "error" && (
          <div className="fhevm-status error">
            <div className="fhevm-status-content">
              <p>⚠️ FHEVM initialization failed</p>
              <p className="error-details">{error?.message}</p>
            </div>
          </div>
        )}

        {isConnected && (
          <nav className="page-nav" aria-label="Primary navigation">
            {pageSections.map((section) => (
              <button
                key={section.id}
                type="button"
                className={`page-nav-item ${activeSection === section.id ? "active" : ""}`}
                onClick={() => setActiveSection(section.id)}
                aria-pressed={activeSection === section.id}
              >
                <span className="nav-icon">{section.icon}</span>
                <div className="nav-copy">
                  <span className="nav-label">{section.label}</span>
                  <span className="nav-description">{section.description}</span>
                </div>
              </button>
            ))}
          </nav>
        )}

        <main className="main-content">
          {!isConnected ? (
            <div className="connect-wallet-container">
              <div className="connect-wallet-card">
                <img src="/logo.svg" alt="Sealed Ballot" className="connect-logo" />
                <h2>Welcome to Sealed Ballot</h2>
                <p className="connect-description">
                  A time-locked encrypted voting system powered by Fully Homomorphic Encryption (FHE).
                  Votes remain encrypted until the deadline, ensuring complete privacy and fairness.
                </p>
                <div className="features">
                  <div className="feature-item">
                    <span className="feature-icon">🔒</span>
                    <span className="feature-text">Encrypted Votes</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">⏰</span>
                    <span className="feature-text">Time-Locked</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">🔓</span>
                    <span className="feature-text">Delayed Decryption</span>
                  </div>
                  <div className="feature-item">
                    <span className="feature-icon">🎯</span>
                    <span className="feature-text">Fair & Transparent</span>
                  </div>
                </div>
                <p className="connect-prompt">Connect your wallet to get started</p>
              </div>
            </div>
          ) : (
            <div className="page-wrapper">
              <section className={`page-panel ${panelState}`} key={activeSection}>
                {renderSection()}
              </section>
            </div>
          )}
        </main>

        <footer className="footer">
          <p>
            Built with ❤️ using{" "}
            <a href="https://docs.zama.ai/fhevm" target="_blank" rel="noopener noreferrer">
              Zama FHEVM
            </a>
          </p>
        </footer>
      </div>
    </FhevmContext.Provider>
  );
}

export default App;
